import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserProvider } from 'ethers';

const AuthContext = createContext();

// Whitelist of allowed addresses (lowercase for comparison)
const ALLOWED_ADDRESSES = [
  '0x10fa00823d930bd4ab3592cded68d830da652d22'
];

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already authenticated from localStorage
    const savedAuth = localStorage.getItem('web3Auth');
    if (savedAuth) {
      const { address: savedAddress, expiry } = JSON.parse(savedAuth);
      if (expiry > Date.now() && isAddressAllowed(savedAddress)) {
        setAddress(savedAddress);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('web3Auth');
      }
    }
    setLoading(false);
  }, []);

  const isAddressAllowed = (addr) => {
    return ALLOWED_ADDRESSES.includes(addr.toLowerCase());
  };

  const connectWallet = async () => {
    setError('');
    
    if (!window.ethereum) {
      setError('Please install MetaMask or another Web3 wallet');
      return false;
    }

    try {
      const provider = new BrowserProvider(window.ethereum);
      
      // Request account access
      const accounts = await provider.send('eth_requestAccounts', []);
      const userAddress = accounts[0];

      // Check if address is in whitelist
      if (!isAddressAllowed(userAddress)) {
        setError('This wallet address is not authorized');
        return false;
      }

      // Create message to sign
      const message = `Sign this message to authenticate with Samuel's 2026\n\nTimestamp: ${Date.now()}`;
      
      // Request signature
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      // Verify signature (the signature itself proves ownership)
      if (signature) {
        setAddress(userAddress);
        setIsAuthenticated(true);
        
        // Save to localStorage with 24h expiry
        localStorage.setItem('web3Auth', JSON.stringify({
          address: userAddress,
          expiry: Date.now() + 24 * 60 * 60 * 1000
        }));
        
        return true;
      }
    } catch (err) {
      console.error('Wallet connection error:', err);
      if (err.code === 4001) {
        setError('You rejected the signature request');
      } else {
        setError('Failed to connect wallet');
      }
      return false;
    }
  };

  const disconnect = () => {
    setIsAuthenticated(false);
    setAddress(null);
    localStorage.removeItem('web3Auth');
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      address,
      loading,
      error,
      connectWallet,
      disconnect
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
